import { z } from 'zod';

/**
 * Realtime event contract (M3). The transport is a Hibernatable WebSocket accepted
 * inside each sale's `SaleDO` (ADR 0007); these are the server→client event shapes and
 * the client→server subscribe protocol from `docs/05-realtime-protocol.md`, defined
 * once here so the DO broadcaster and both React apps can never drift.
 *
 * Wire conventions match the REST contract: **timestamps are epoch-ms integers**
 * (superseding the older ISO-8601 annotations in the doc) and **money is integer
 * kuruş**. `conference.available` ships in M5 (provider-agnostic; see ADR 0018).
 */

const rescheduleEntrySchema = z.object({
  lotId: z.uuid(),
  newStartDate: z.number().int(), // epoch-ms
  newEndDate: z.number().int(), // epoch-ms
});
export type RescheduleEntry = z.infer<typeof rescheduleEntrySchema>;

// --- Server → client events -------------------------------------------------

/** Emitted to `sale:{id}` and `lot:{id}` after every committed bid. */
export const bidPlacedEventSchema = z.object({
  type: z.literal('bid.placed'),
  saleId: z.uuid(),
  lotId: z.uuid(),
  amount: z.number().int(), // kuruş
  bidderName: z.string().optional(), // anonymised (first name + last initial) for non-admin
  placedAt: z.number().int(), // epoch-ms
  priceGoalReached: z.boolean(), // currentTopBid >= lot.targetPrice
  bidCount: z.number().int(),
  /**
   * The effective step the **next** bid on this lot will add, in kuruş (M19).
   *
   * Carried because the step is not constant: it changes as the price crosses an increment
   * band, and again once the reserve is crossed where a `post_threshold_increment` is set. A
   * client that overlaid `amount` from this event but kept the increment from its REST
   * snapshot would compute the wrong next figure — and, since M19 has it send that figure as
   * `expectedAmount`, would be rejected on every subsequent bid until it refetched.
   *
   * Optional so an older client keeps parsing the envelope.
   */
  nextIncrement: z.number().int().optional(), // kuruş
});
export type BidPlacedEvent = z.infer<typeof bidPlacedEventSchema>;

/** Emitted to `sale:{id}` and `lot:{id}` when a lot transitions `SCHEDULED → OPEN`. */
export const lotOpenedEventSchema = z.object({
  type: z.literal('lot.opened'),
  saleId: z.uuid(),
  lotId: z.uuid(),
  openedAt: z.number().int(), // epoch-ms
  endsAt: z.number().int(), // epoch-ms
});
export type LotOpenedEvent = z.infer<typeof lotOpenedEventSchema>;

/** Emitted to `sale:{id}` and `lot:{id}` when a lot transitions to `SOLD` or `UNSOLD`. */
export const lotClosedEventSchema = z.object({
  type: z.literal('lot.closed'),
  saleId: z.uuid(),
  lotId: z.uuid(),
  finalState: z.enum(['SOLD', 'UNSOLD']),
  finalPrice: z.number().int().optional(), // kuruş
  winnerId: z.uuid().optional(), // absent for UNSOLD
  closedAt: z.number().int(), // epoch-ms
  nextLotId: z.uuid().optional(), // set if there is a next lot in a serial auction
});
export type LotClosedEvent = z.infer<typeof lotClosedEventSchema>;

/**
 * Emitted to `sale:{id}` and `lot:{id}` when a late bid on a take-control lot extends the
 * countdown (soft-close / anti-sniping, M4.5). `newEndsAt` is the pushed-out close time.
 */
export const lotExtendedEventSchema = z.object({
  type: z.literal('lot.extended'),
  saleId: z.uuid(),
  lotId: z.uuid(),
  newEndsAt: z.number().int(), // epoch-ms
});
export type LotExtendedEvent = z.infer<typeof lotExtendedEventSchema>;

/** Emitted to `sale:{id}` when an admin pauses the sale. */
export const salePausedEventSchema = z.object({
  type: z.literal('sale.paused'),
  saleId: z.uuid(),
  pausedOnLotId: z.uuid().nullable(),
  secondsRemaining: z.number().int(),
  pausedAt: z.number().int(), // epoch-ms
});
export type SalePausedEvent = z.infer<typeof salePausedEventSchema>;

/** Emitted to `sale:{id}` when an admin resumes the sale (schedule recomputed). */
export const saleResumedEventSchema = z.object({
  type: z.literal('sale.resumed'),
  saleId: z.uuid(),
  resumedAt: z.number().int(), // epoch-ms
  reschedule: z.array(rescheduleEntrySchema),
});
export type SaleResumedEvent = z.infer<typeof saleResumedEventSchema>;

/** Emitted to `sale:{id}` when an admin removes a lot mid-auction (schedule recomputed). */
export const lotRemovedEventSchema = z.object({
  type: z.literal('lot.removed'),
  saleId: z.uuid(),
  lotId: z.uuid(),
  reschedule: z.array(rescheduleEntrySchema),
});
export type LotRemovedEvent = z.infer<typeof lotRemovedEventSchema>;

/** Emitted to `sale:{id}` when the entire sale transitions to `CLOSED`. */
export const saleClosedEventSchema = z.object({
  type: z.literal('sale.closed'),
  saleId: z.uuid(),
  closedAt: z.number().int(), // epoch-ms
});
export type SaleClosedEvent = z.infer<typeof saleClosedEventSchema>;

/** Emitted to `user:{userId}` when the user is displaced from the top position. */
export const outbidEventSchema = z.object({
  type: z.literal('outbid'),
  lotId: z.uuid(),
  saleId: z.uuid(),
  newTopAmount: z.number().int(), // kuruş
  yourLastBid: z.number().int(), // kuruş
});
export type OutbidEvent = z.infer<typeof outbidEventSchema>;

/** Emitted to `user:{userId}` when the lot closes and the user is the winner. */
export const youWonEventSchema = z.object({
  type: z.literal('you-won'),
  lotId: z.uuid(),
  saleId: z.uuid(),
  finalPrice: z.number().int(), // kuruş
});
export type YouWonEvent = z.infer<typeof youWonEventSchema>;

/**
 * Emitted to `sale:{id}` (+ `lot:{id}` for a per-lot room) shortly before a lot closes
 * (`endDate − 120 s`), carrying the provider-agnostic join URL so watching bidders can enter
 * the live conference (M5, ADR 0018). Fired once per room. `lotId` is absent for a joint
 * (sale-level) conference.
 */
export const conferenceAvailableEventSchema = z.object({
  type: z.literal('conference.available'),
  saleId: z.uuid(),
  lotId: z.uuid().optional(), // absent for a joint (sale-level) conference
  joinUrl: z.string(),
  availableAt: z.number().int(), // epoch-ms
});
export type ConferenceAvailableEvent = z.infer<typeof conferenceAvailableEventSchema>;

/** Discriminated union of every server→client event, keyed on `type`. */
export const serverEventSchema = z.discriminatedUnion('type', [
  bidPlacedEventSchema,
  lotOpenedEventSchema,
  lotClosedEventSchema,
  lotExtendedEventSchema,
  salePausedEventSchema,
  saleResumedEventSchema,
  lotRemovedEventSchema,
  saleClosedEventSchema,
  outbidEventSchema,
  youWonEventSchema,
  conferenceAvailableEventSchema,
]);
export type ServerEvent = z.infer<typeof serverEventSchema>;

// --- Client → server commands -----------------------------------------------

/**
 * Sent by a connected client to join/leave `lot:{lotId}` streams. `sale:{saleId}` is
 * implicit (one DO instance is the sale's room) and `user:{userId}` needs no opt-in —
 * those events target the authenticated socket directly.
 */
export const clientCommandSchema = z.object({
  type: z.enum(['subscribe', 'unsubscribe']),
  // Bounded per message; the DO also caps the total *stored* subscriptions so the
  // per-socket attachment stays within the runtime's 2 KB `serializeAttachment` limit.
  rooms: z.array(z.string().max(80)).max(50),
});
export type ClientCommand = z.infer<typeof clientCommandSchema>;
